import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, parseISO, isWithinInterval } from 'date-fns';
import { 
  PlusCircle, Calendar, List, LayoutGrid, ChevronRight,
  CalendarDays, Clock, Stethoscope, Video, Phone, UserPlus, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from 'sonner';
import { AppointmentType } from '@/types/appointment';
import { Patient } from '@/types/doctor';
import { usePatients } from '@/hooks/usePatients';
import AppointmentsList from './components/AppointmentsList';
import AppointmentFilters from './components/AppointmentFilters';
import AppointmentCalendar from './components/AppointmentCalendar';

interface LocalAppointment {
  id: string;
  patientId: string;
  doctorId: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentType: AppointmentType;
  status: 'scheduled' | 'confirmed' | 'completed' | 'missed' | 'canceled' | 'cancelled' | 'no_show';
  duration: number;
  notes?: string;
  patientName?: string;
  date?: string;
  type?: AppointmentType;
  time?: string;
}

const mockAppointmentsData: LocalAppointment[] = [
  {
    id: '1',
    patientId: '1',
    doctorId: '123',
    appointmentDate: '2025-04-15',
    appointmentTime: '10:00',
    appointmentType: 'clinic',
    status: 'scheduled',
    duration: 30,
    notes: 'Follow-up checkup after crown placement',
    patientName: 'John Smith',
    date: new Date('2025-04-15T10:00:00').toISOString(),
    type: 'clinic',
    time: '10:00'
  },
  {
    id: '2',
    patientId: '2',
    doctorId: '123',
    appointmentDate: '2025-04-16',
    appointmentTime: '14:30',
    appointmentType: 'video',
    status: 'scheduled',
    duration: 20,
    notes: 'Veneers consultation',
    patientName: 'Emily Johnson',
    date: new Date('2025-04-16T14:30:00').toISOString(),
    type: 'video',
    time: '14:30'
  },
  {
    id: '3',
    patientId: '3',
    doctorId: '123',
    appointmentDate: '2025-04-20',
    appointmentTime: '11:15',
    appointmentType: 'voice',
    status: 'scheduled',
    duration: 15,
    notes: 'Post-treatment discussion',
    patientName: 'Michael Brown',
    date: new Date('2025-04-20T11:15:00').toISOString(),
    type: 'voice',
    time: '11:15'
  },
  {
    id: '4',
    patientId: '4',
    doctorId: '123',
    appointmentDate: '2025-04-20',
    appointmentTime: '09:00',
    appointmentType: 'clinic',
    status: 'scheduled',
    duration: 45,
    notes: 'Root canal follow-up appointment',
    patientName: 'Sarah Wilson',
    date: new Date('2025-04-20T09:00:00').toISOString(),
    type: 'clinic',
    time: '09:00'
  }
];

const DoctorAppointmentsPage: React.FC = () => {
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [appointments, setAppointments] = useState<LocalAppointment[]>(mockAppointmentsData);
  const [filteredAppointments, setFilteredAppointments] = useState<LocalAppointment[]>(mockAppointmentsData);
  const [isLoading, setIsLoading] = useState(true);
  const { patients } = usePatients();
  const navigate = useNavigate();
  
  const [newAppointment, setNewAppointment] = useState({
    patientId: '',
    date: new Date(),
    time: '09:00',
    type: 'clinic' as AppointmentType,
    notes: '',
  });
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  
  const [activeFilters, setActiveFilters] = useState({
    clinic: true,
    video: true,
    voice: true,
    dateRange: {
      from: new Date(),
      to: addDays(new Date(), 14)
    }
  });

  useEffect(() => {
    setTimeout(() => {
      setAppointments(mockAppointmentsData);
      setFilteredAppointments(mockAppointmentsData);
      setIsLoading(false);
    }, 1000);
  }, []);

  const handleReschedule = (id: string, newDate?: Date) => {
    const rescheduleDate = newDate || addDays(new Date(), 1);
    
    setAppointments(prev => 
      prev.map(appointment => 
        appointment.id === id 
          ? { 
              ...appointment, 
              date: rescheduleDate.toISOString(),
              appointmentDate: format(rescheduleDate, 'yyyy-MM-dd')
            } 
          : appointment
      )
    );
    
    toast.success('Appointment rescheduled successfully');
  };

  const handleCancel = (id: string) => {
    setAppointments(prev => prev.filter(appointment => appointment.id !== id));
    toast.success('Appointment cancelled successfully');
  };
  
  const handleCreateAppointment = () => {
    if (!newAppointment.patientId) {
      toast.error('Please select a patient');
      return;
    }
    
    const selectedPatient = patients.find(p => p.id === newAppointment.patientId);
    if (!selectedPatient) {
      toast.error('Selected patient not found');
      return;
    }
    
    const appointmentDateTime = new Date(newAppointment.date);
    const [hours, minutes] = newAppointment.time.split(':').map(Number);
    appointmentDateTime.setHours(hours, minutes);
    
    const newAppointmentData: LocalAppointment = {
      id: `temp-${Date.now()}`,
      patientId: newAppointment.patientId,
      doctorId: 'current-doctor',
      appointmentDate: format(appointmentDateTime, 'yyyy-MM-dd'),
      appointmentTime: newAppointment.time,
      appointmentType: newAppointment.type as AppointmentType,
      duration: 30,
      status: 'scheduled',
      notes: newAppointment.notes || undefined,
      patientName: selectedPatient.name,
      date: appointmentDateTime.toISOString(),
      time: newAppointment.time,
      type: newAppointment.type as AppointmentType
    };
    
    setAppointments(prev => [...prev, newAppointmentData]);
    
    setNewAppointment({
      patientId: '',
      date: new Date(),
      time: '09:00',
      type: 'clinic' as AppointmentType,
      notes: '',
    });
    
    setIsCreateModalOpen(false);
    toast.success('Appointment created successfully');
  };

  const handleViewDetails = (appointmentId: string) => {
    const appointment = appointments.find(app => app.id === appointmentId);
    if (!appointment) return;
    
    if (appointment.patientId) {
      const patient = patients.find(p => p.id === appointment.patientId);
      
      if (patient) {
        if (appointment.type === 'clinic' || appointment.appointmentType === 'clinic') {
          navigate(`/doctor-area/treatment-sessions`);
        } else if (appointment.type === 'video' || appointment.appointmentType === 'video') {
          navigate(`/doctor-area/case-file/${patient.id}`);
        } else if (appointment.type === 'voice' || appointment.appointmentType === 'voice') {
          navigate(`/doctor-area/follow-up-reports`);
        } else {
          navigate(`/doctor-area/case-file/${patient.id}`);
        }
      }
    }
  };

  const handleCreateAppointmentWithDate = (date?: Date) => {
    if (date) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      setNewAppointment({
        ...newAppointment,
        date: date,
        time: `${hours}:${minutes}`
      });
    }
    
    setIsCreateModalOpen(true);
  };

  return (
    <div className="container mx-auto p-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Appointments</h1>
          <p className="text-muted-foreground">Manage and schedule patient appointments</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="hidden sm:flex items-center space-x-1 rounded-md border p-1">
            <Button 
              variant={view === 'list' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setView('list')}
            >
              <List className="h-4 w-4 mr-1" />
              List
            </Button>
            <Button 
              variant={view === 'calendar' ? 'default' : 'ghost'} 
              size="sm" 
              onClick={() => setView('calendar')}
            >
              <Calendar className="h-4 w-4 mr-1" />
              Calendar
            </Button>
          </div>
          
          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button>
                <PlusCircle className="h-4 w-4 mr-2" />
                New Appointment
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create New Appointment</DialogTitle>
                <DialogDescription>
                  Schedule a new appointment with a patient.
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <label htmlFor="patient" className="text-sm font-medium">Patient</label>
                  <Select 
                    value={newAppointment.patientId} 
                    onValueChange={(value) => setNewAppointment({...newAppointment, patientId: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map((patient) => (
                        <SelectItem key={patient.id} value={patient.id.toString()}>
                          {patient.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <label htmlFor="date" className="text-sm font-medium">Date</label>
                    <DatePicker
                      date={newAppointment.date}
                      setDate={(date) => setNewAppointment({...newAppointment, date: date || new Date()})}
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <label htmlFor="time" className="text-sm font-medium">Time</label>
                    <Input
                      type="time"
                      id="time"
                      value={newAppointment.time}
                      onChange={(e) => setNewAppointment({...newAppointment, time: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="grid gap-2">
                  <label htmlFor="type" className="text-sm font-medium">Appointment Type</label>
                  <Select 
                    value={newAppointment.type} 
                    onValueChange={(value: AppointmentType) => setNewAppointment({...newAppointment, type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clinic">
                        <div className="flex items-center">
                          <Stethoscope className="h-4 w-4 mr-2 text-green-500" />
                          <span>In-Clinic</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="video">
                        <div className="flex items-center">
                          <Video className="h-4 w-4 mr-2 text-blue-500" />
                          <span>Video Call</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="voice">
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-2 text-purple-500" />
                          <span>Voice Call</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid gap-2">
                  <label htmlFor="notes" className="text-sm font-medium">Notes</label>
                  <Textarea
                    id="notes"
                    placeholder="Appointment notes..."
                    value={newAppointment.notes}
                    onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
                  />
                </div>
              </div>
              
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button onClick={handleCreateAppointment}>Create Appointment</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      <AppointmentFilters
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        appointments={appointments}
        setFilteredAppointments={setFilteredAppointments}
      />
      
      <Tabs defaultValue="upcoming" className="space-y-4">
        <TabsList>
          <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="past">Past</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upcoming" className="space-y-4">
          {view === 'list' ? (
            <AppointmentsList 
              appointments={filteredAppointments} 
              onReschedule={handleReschedule} 
              onCancel={handleCancel} 
            />
          ) : (
            <AppointmentCalendar
              appointments={filteredAppointments}
              onCreateAppointment={handleCreateAppointmentWithDate}
              onViewDetails={handleViewDetails}
              onReschedule={handleReschedule}
            />
          )}
        </TabsContent>
        
        <TabsContent value="past" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Past Appointments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6 text-muted-foreground">
                No past appointments to display
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="cancelled" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cancelled Appointments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6 text-muted-foreground">
                No cancelled appointments to display
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DoctorAppointmentsPage;
